import { useEffect, useState } from 'react';
import { materialInfoApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from './ConfirmDialog';
import LoadingSpinner from './LoadingSpinner';

// Material Information key management — opened from Master Data (Materials
// or Stationery). Keys are shared across every individually-tracked unit of
// this one material/stationery type; values (per unit) are edited elsewhere,
// wherever that unit is shown — see MaterialInfoPanel.
export default function MaterialInfoFieldsModal({ show, onClose, kitId, stationeryItemId, itItemId, materialName }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(kitId ? 'materials_info_manage' : stationeryItemId ? 'stationery_info_manage' : 'it_info_manage');

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingKey, setEditingKey] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Rename/Delete only surface once a key is clicked open — not shown
  // inline on every row by default.
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!show) return;
    setError('');
    setNewKey('');
    setEditingId(null);
    setExpandedId(null);
    load();
  }, [show, kitId, stationeryItemId, itItemId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = kitId ? { kitId } : stationeryItemId ? { stationeryItemId } : { itItemId };
      const res = await materialInfoApi.listFields(params);
      setFields(res.data.data);
    } catch {
      setError('Failed to load Material Information fields');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newKey.trim()) return;
    setSaving(true);
    setError('');
    try {
      await materialInfoApi.createField(kitId ? { kitId, key: newKey.trim() } : stationeryItemId ? { stationeryItemId, key: newKey.trim() } : { itItemId, key: newKey.trim() });
      setNewKey('');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add field');
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id) => {
    if (!editingKey.trim()) return;
    setSaving(true);
    setError('');
    try {
      await materialInfoApi.updateField(id, { key: editingKey.trim() });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to rename field');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    setError('');
    try {
      await materialInfoApi.deleteField(target.id);
      setExpandedId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete field');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold mb-1">Material Information</h3>
          <p className="text-sm text-gray-500 mb-4">{materialName}</p>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="space-y-2 mb-4 max-h-80 overflow-y-auto">
              {fields.length === 0 && <p className="text-sm text-gray-400">No custom fields added yet.</p>}
              {fields.map((f) => (
                <div key={f.id} className="flex items-center gap-2 border border-gray-100 rounded-lg px-3 py-2">
                  {editingId === f.id ? (
                    <>
                      <input
                        className="input flex-1"
                        value={editingKey}
                        onChange={(e) => setEditingKey(e.target.value)}
                        autoFocus
                      />
                      <button className="text-blue-600 hover:underline text-sm" onClick={() => handleRename(f.id)} disabled={saving}>Save</button>
                      <button className="text-gray-500 hover:underline text-sm" onClick={() => setEditingId(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button
                        className="flex-1 text-left text-sm font-medium text-gray-800 hover:text-blue-600"
                        onClick={() => setExpandedId((id) => (id === f.id ? null : f.id))}
                      >
                        {f.key}
                      </button>
                      {canManage && expandedId === f.id && (
                        <>
                          <button
                            className="text-blue-600 hover:underline text-sm"
                            onClick={() => { setEditingId(f.id); setEditingKey(f.key); }}
                          >Rename</button>
                          <button
                            className="text-red-600 hover:underline text-sm"
                            onClick={() => setDeleteTarget(f)}
                          >Delete</button>
                        </>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {canManage && (
            <form onSubmit={handleAdd} className="flex gap-2 mb-4">
              <input
                className="input flex-1"
                placeholder="New field name (e.g. RAM)"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
              <button type="submit" className="btn-secondary" disabled={saving || !newKey.trim()}>+ Add Field</button>
            </form>
          )}

          {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

          <div className="flex gap-3 justify-end">
            <button type="button" className="btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this field?"
        message={`"${deleteTarget?.key}" and its value on every individual unit of ${materialName} will be permanently removed.`}
        danger
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
